// src/cockpit4/components/HermesLearningGrowth.tsx
// ============================================
// 学习与成长（Hermes 助手 P2-功能9）- v2.0 详细改进计划
// 基于老板关注点主动学习 / 行业动态推送 / 竞品情报 / 个人知识管理 / 学习计划
// 数据来源：localStorage（xeaos_hermes_learning）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Brain, TrendingUp, Radio, FileText, GraduationCap, CheckCircle2 } from "lucide-react";

interface LearningTopic {
  id: string;
  name: string;
  focus: string;
  progress: number;
  articles: number;
  status: string;
}

const INITIAL_TOPICS: LearningTopic[] = [
  { id: "t1", name: "AI 企业应用", focus: "老板重点关注：AI Agent 落地", progress: 72, articles: 12, status: "进行中" },
  { id: "t2", name: "企业数字化经营", focus: "行业动态：数字化经营方法论", progress: 85, articles: 18, status: "进行中" },
  { id: "t3", name: "私域运营实战", focus: "营销体系：私域增长策略", progress: 45, articles: 8, status: "进行中" },
];

const NEWS = [
  { id: "n1", title: "DeepSeek 发布新模型，企业级推理能力大幅提升", source: "行业动态", time: "2小时前" },
  { id: "n2", title: "2026 企业 AI 采用率报告：70% 企业进入落地阶段", source: "研究报告", time: "5小时前" },
  { id: "n3", title: "竞品 X 更新了工作流自动化功能", source: "竞品情报", time: "昨天" },
];

const KEY = "xeaos_hermes_learning";

function load(): LearningTopic[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as LearningTopic[];
  } catch { /* ignore */ }
  return INITIAL_TOPICS;
}

export default function HermesLearningGrowth() {
  const [topics, setTopics] = useState<LearningTopic[]>(load);
  const [qDone, setQDone] = useState(false);

  const stats = useMemo(() => {
    const avgProgress = topics.reduce((a, t) => a + t.progress, 0) / topics.length;
    const totalArticles = topics.reduce((a, t) => a + t.articles, 0);
    return { avgProgress, totalArticles };
  }, [topics]);

  const startArticle = (id: string) => {
    setTopics((prev) => prev.map((t) => (t.id === id ? { ...t, progress: Math.min(100, t.progress + 4), articles: t.articles + 1 } : t)));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Brain size={14} className="text-violet-400" />
        <h3 className="text-[12px] font-medium text-white">Hermes 学习与成长</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能9 · Learning</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="平均学习进度" value={`${stats.avgProgress.toFixed(0)}%`} color="#34D399" />
        <MetricCard label="已读文章" value={`${stats.totalArticles} 篇`} color="#38BDF8" />
        <MetricCard label="学习主题" value={`${topics.length} 个`} color="#A78BFA" />
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <GraduationCap size={11} className="text-violet-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">主动学习主题 · 基于老板关注</span>
          </div>
          <div className="space-y-1.5">
            {topics.map((t) => (
              <div key={t.id} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-white">{t.name}</span>
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300">{t.status}</span>
                </div>
                <div className="text-[8px] font-mono text-gray-600 mt-0.5">{t.focus}</div>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${t.progress}%`, background: "#A78BFA" }} />
                  </div>
                  <span className="text-[9px] font-mono text-gray-400">{t.progress}%</span>
                  <span className="text-[8px] font-mono text-gray-600">{t.articles} 篇</span>
                  <button onClick={() => startArticle(t.id)} className="text-[8px] font-mono px-1.5 py-0.5 rounded border border-violet-500/30 bg-violet-500/10 text-violet-300">学习</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Radio size={11} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">行业动态与竞品情报</span>
          </div>
          <div className="space-y-1.5">
            {NEWS.map((n) => (
              <div key={n.id} className="rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-2">
                <div className="text-[10px] text-gray-300">{n.title}</div>
                <div className="text-[8px] font-mono text-gray-600 mt-0.5">{n.source} · {n.time}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2 rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
            <FileText size={10} className="text-cyan-400" />
            <span className="text-[9px] font-mono text-gray-500">Hermes 已自动归档 24 篇学习笔记至知识库</span>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp size={11} className="text-amber-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">知识问答自测</span>
        </div>
        {!qDone ? (
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-300 flex-1">Q: 为什么「Obsidian 唯一大脑」原则如此重要？</span>
            <button onClick={() => setQDone(true)} className="px-3 py-1 rounded-md border border-amber-500/30 bg-amber-500/10 text-[9px] font-mono text-amber-300">查看答案</button>
          </div>
        ) : (
          <div className="flex items-start gap-2 text-[10px] text-gray-300 leading-relaxed">
            <CheckCircle2 size={11} className="text-emerald-400 mt-0.5 flex-shrink-0" />
            <span>因为所有 Runtime 必须从 Vault 读取真相源，避免各模块数据不一致——这是 XEAOS 架构的基石。</span>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

